import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Users, Truck, Route } from "lucide-react";

const iso = (d) => d.toISOString().substring(0, 10);
const meno = (giorni) => {
  const d = new Date();
  d.setDate(d.getDate() - giorni);
  return iso(d);
};
const PERIODI = [
  { l: "30 giorni", g: 30 }, { l: "90 giorni", g: 90 },
  { l: "6 mesi", g: 182 }, { l: "1 anno", g: 365 },
];

/**
 * Cantiere e committente: quante risorse ha assorbito ciascuno.
 *
 * Si contano giornate, non euro: in archivio non esiste nessun costo orario o
 * giornaliero, e mettere un importo qui vorrebbe dire inventarlo.
 */
export default function ReportCantieri() {
  const [da, setDa] = useState(meno(90));
  const [a, setA] = useState(iso(new Date()));
  const [dati, setDati] = useState(null);
  const [caricando, setCaricando] = useState(true);

  const carica = useCallback(async () => {
    setCaricando(true);
    try {
      const { data } = await api.get(`/reports/cantieri?date_from=${da}&date_to=${a}`);
      setDati(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento report cantieri");
    } finally { setCaricando(false); }
  }, [da, a]);

  useEffect(() => { carica(); }, [carica]);

  const cifra = (n) => Number(n || 0).toLocaleString("it-IT");

  return (
    <Card className="border-slate-200">
      <CardContent className="p-6 space-y-4">
        <div>
          <h3 className="font-display text-lg font-bold">Cantieri e committenti</h3>
          <p className="text-xs text-slate-500 mt-1">
            Quante giornate di persone e di mezzi ha assorbito ogni cantiere, e quanto pesa
            ciascun cliente.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap items-end">
          <div>
            <Label className="text-xs">Dal</Label>
            <Input type="date" className="h-10 mt-1" value={da}
                   onChange={(e) => setDa(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Al</Label>
            <Input type="date" className="h-10 mt-1" value={a}
                   onChange={(e) => setA(e.target.value)} />
          </div>
          <div className="flex gap-1 flex-wrap">
            {PERIODI.map(p => (
              <button key={p.g} type="button"
                      onClick={() => { setDa(meno(p.g)); setA(iso(new Date())); }}
                      className="px-3 py-2 rounded-full text-xs font-medium border bg-white
                                 text-slate-600 border-slate-200 hover:border-slate-300">
                {p.l}
              </button>
            ))}
          </div>
        </div>

        {caricando && <p className="text-sm text-slate-500 py-6 text-center">Caricamento...</p>}

        {!caricando && dati && (<>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { i: Users, l: "Giornate persona", v: cifra(dati.totale_giornate_uomo) },
              { i: Truck, l: "Giornate mezzo", v: cifra(dati.totale_giornate_mezzo) },
              { i: Route, l: "Km stimati", v: cifra(dati.totale_km_stimati) },
              { i: Building2, l: "Cantieri", v: dati.cantieri.length },
            ].map(x => (
              <div key={x.l} className="border border-slate-200 rounded p-3">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500">
                  <x.i className="w-3 h-3" />{x.l}
                </div>
                <div className="font-display text-xl font-black mt-1">{x.v}</div>
              </div>
            ))}
          </div>

          {dati.cantieri.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">
              Nessuna giornata pianificata nel periodo scelto.
            </p>
          ) : (<>
            <div>
              <h4 className="font-display font-bold text-sm mb-2">Per committente</h4>
              <div className="space-y-2">
                {dati.committenti.map(c => {
                  const quota = dati.totale_giornate_uomo > 0
                    ? (c.giornate_uomo / dati.totale_giornate_uomo) * 100 : 0;
                  return (
                    <div key={c.committente}>
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="font-semibold truncate">{c.committente}</span>
                        <span className="font-display font-black whitespace-nowrap">
                          {cifra(c.giornate_uomo)} giornate
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded mt-1 overflow-hidden">
                        <div className="h-full bg-[#0067b2] rounded"
                             style={{ width: `${Math.max(quota, c.giornate_uomo > 0 ? 1 : 0)}%` }} />
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {quota.toFixed(1)}% · {c.cantieri} cantier{c.cantieri === 1 ? "e" : "i"} ·{" "}
                        {c.giorni} giorni · {cifra(c.giornate_mezzo)} giornate mezzo ·{" "}
                        {cifra(c.km_stimati)} km stimati
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="font-display font-bold text-sm mb-2">Per cantiere</h4>
              <div className="border border-slate-200 rounded overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="text-left p-2">Cantiere</th>
                      <th className="text-left p-2">Committente</th>
                      <th className="text-right p-2">Giorni</th>
                      <th className="text-right p-2">Giornate persona</th>
                      <th className="text-right p-2">Giornate mezzo</th>
                      <th className="text-right p-2">Km stimati</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dati.cantieri.map(r => (
                      <tr key={r.site_id} className="border-t border-slate-100">
                        <td className="p-2">
                          <div className="font-medium">{r.cantiere}</div>
                          <div className="text-[10px] text-slate-400">
                            {r.squadre.join(", ") || "—"}
                          </div>
                        </td>
                        <td className="p-2 text-slate-600">{r.committente}</td>
                        <td className="p-2 text-right">{r.giorni}</td>
                        <td className="p-2 text-right font-semibold">
                          {cifra(r.giornate_uomo)}
                          <div className="text-[10px] text-slate-400 font-normal">
                            {r.persone_diverse} persone
                          </div>
                        </td>
                        <td className="p-2 text-right">
                          {cifra(r.giornate_mezzo)}
                          <div className="text-[10px] text-slate-400">
                            {r.mezzi_diversi} mezzi
                          </div>
                        </td>
                        <td className="p-2 text-right">
                          {r.km_stimati == null
                            ? <span className="text-slate-400 text-xs">distanza non nota</span>
                            : cifra(r.km_stimati)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-[11px] text-slate-500 space-y-1">
              <p>
                <b>Giornate persona</b>: una persona per un giorno vale uno. <b>Km stimati</b>:
                andata e ritorno dalla sede per ogni giorno di presenza, sulla distanza salvata
                sul cantiere. È una stima dei trasferimenti, non il chilometraggio reale dei
                mezzi: non va sommata ai km letti sul contachilometri.
              </p>
              {dati.cantieri_senza_distanza > 0 && (
                <p>
                  {dati.cantieri_senza_distanza} cantier{dati.cantieri_senza_distanza === 1 ? "e" : "i"} non
                  {dati.cantieri_senza_distanza === 1 ? " ha" : " hanno"} la distanza salvata: i loro km
                  non sono conteggiati. Si sistema riselezionando l'indirizzo dai suggerimenti.
                </p>
              )}
              {dati.giornate_con_piu_tappe > 0 && (
                <p>
                  {dati.giornate_con_piu_tappe} giornate avevano più tappe: sono state attribuite
                  al primo cantiere della giornata.
                </p>
              )}
            </div>
          </>)}
        </>)}
      </CardContent>
    </Card>
  );
}
